import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  selectApplicationId,
  selectToken,
  selectTemplateData,
} from 'legacyfrontend/redux/selectors/application-selectors';
import aplicacionesApi from 'legacyfrontend/api/aplicaciones';
import { marcaBlancaApiClient } from 'legacyfrontend/api/client';
import { marcaBlancaApi } from 'legacyfrontend/api/services/endpoints';
import {
  getCachedData,
  updateCacheIfChanged,
  removeCachedData,
  getCacheInfo,
} from 'legacyfrontend/helpers/cacheHelper';
import { isClient } from 'legacyfrontend/helpers/ssr-helper';

const CACHE_CONFIG = {
  accionesAplicacion: {
    key: 'acciones_aplicacion',
    ttl: 5 * 60 * 1000,
    enabled: true,
  },
  templateData: {
    key: 'template_data',
    ttl: 30 * 60 * 1000,
    enabled: true,
  },
};

const defaultCacheConfigValues = {
  accionesAplicacion: [],
  isLoadingAcciones: true,
  accionesError: null,
  refetchAcciones: () => Promise.resolve(),
  clearAccionesCache: () => {},
  templateData: null,
  isLoadingTemplate: true,
  templateError: null,
  refetchTemplate: () => Promise.resolve(),
  clearTemplateCache: () => {},
  getCacheStatus: () => null,
  fetchWithCache: () => Promise.resolve({ data: null, fromCache: false }),
  clearDynamicCache: () => {},
};

const CacheConfigContext = createContext(defaultCacheConfigValues);
export const CacheConfigProvider = ({ children }) => {
  const applicationId = useSelector(selectApplicationId);
  const token = useSelector(selectToken);
  const reduxTemplateData = useSelector(selectTemplateData);
  
  const [accionesAplicacion, setAccionesAplicacion] = useState([]);
  const [isLoadingAcciones, setIsLoadingAcciones] = useState(true);
  const [accionesError, setAccionesError] = useState(null);

  const [templateData, setTemplateData] = useState(null);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(true);
  const [templateError, setTemplateError] = useState(null);

  const fetchAccionesAplicacion = useCallback(async (forceRefresh = false) => {
    if (!applicationId || !token) {
      setIsLoadingAcciones(false);
      return;
    }

    const config = CACHE_CONFIG.accionesAplicacion;
    
    if (!config.enabled && !forceRefresh) {
      setIsLoadingAcciones(false);
      return;
    }

    try {
      setIsLoadingAcciones(true);
      setAccionesError(null);

      if (!forceRefresh) {
        const cachedData = getCachedData(config.key);
        if (cachedData) {
          setAccionesAplicacion(cachedData);
          setIsLoadingAcciones(false);
          
          setTimeout(() => {
            fetchAccionesAplicacionInBackground();
          }, 0);
          
          return;
        }
      }

      const response = await aplicacionesApi.getAccionesAplicacion(applicationId, token);
      
      let newData = [];
      if (Array.isArray(response)) {
        newData = response;
      } else if (response && Array.isArray(response.data)) {
        newData = response.data;
      } else if (response && Array.isArray(response.object)) {
        newData = response.object;
      }

      const { updated, data } = updateCacheIfChanged(config.key, newData, config.ttl);
      
      if (updated || !accionesAplicacion.length) {
        setAccionesAplicacion(data);
      }
      
    } catch (error) {
      console.error('Error fetching acciones aplicacion:', error);
      setAccionesError(error.message || 'Error al cargar acciones');
      
      const cachedData = getCachedData(config.key);
      if (cachedData) {
        setAccionesAplicacion(cachedData);
      }
    } finally {
      setIsLoadingAcciones(false);
    }
  }, [applicationId, token, accionesAplicacion.length]);

  const fetchAccionesAplicacionInBackground = useCallback(async () => {
    if (!applicationId || !token) {
      return;
    }

    const config = CACHE_CONFIG.accionesAplicacion;

    try {
      const response = await aplicacionesApi.getAccionesAplicacion(applicationId, token);
      
      let newData = [];
      if (Array.isArray(response)) {
        newData = response;
      } else if (response && Array.isArray(response.data)) {
        newData = response.data;
      } else if (response && Array.isArray(response.object)) {
        newData = response.object;
      }

      const { updated, data } = updateCacheIfChanged(config.key, newData, config.ttl);
      
      if (updated) {
        setAccionesAplicacion(data);
      }
      
    } catch (error) {
      console.warn('Error en background fetch de acciones:', error);
    }
  }, [applicationId, token]);

  const refetchAcciones = useCallback(() => {
    return fetchAccionesAplicacion(true);
  }, [fetchAccionesAplicacion]);

  const clearAccionesCache = useCallback(() => {
    removeCachedData(CACHE_CONFIG.accionesAplicacion.key);
    setAccionesAplicacion([]);
  }, []);

  const getCacheStatus = useCallback((resource = 'accionesAplicacion') => {
    const config = CACHE_CONFIG[resource];
    if (!config) return null;
    
    if (config.key) {
      return getCacheInfo(config.key);
    }
    return null;
  }, []);

  /**
   * Fetch dinámico con caché para recursos que requieren parámetros
   * @param {string} resource - Nombre del recurso en CACHE_CONFIG
   * @param {string} cacheKey - Clave completa del caché (incluye parámetros)
   * @param {Function} fetchFn - Función que hace el fetch a la API
   * @param {boolean} forceRefresh - Forzar recarga sin caché
   * @returns {Promise<{data: any, fromCache: boolean}>}
   */
  const fetchWithCache = useCallback(async (resource, cacheKey, fetchFn, forceRefresh = false) => {
    const config = CACHE_CONFIG[resource];
    if (!config || !config.enabled) {
      const data = await fetchFn();
      return { data, fromCache: false };
    }

    try {
      if (!forceRefresh) {
        const cachedData = getCachedData(cacheKey);
        if (cachedData) {
          return { data: cachedData, fromCache: true };
        }
      }

      const data = await fetchFn();
      const { data: cachedData } = updateCacheIfChanged(cacheKey, data, config.ttl);
      return { data: cachedData, fromCache: false };
    } catch (error) {
      const cachedData = getCachedData(cacheKey);
      if (cachedData) {
        return { data: cachedData, fromCache: true };
      }
      throw error;
    }
  }, []);

  /**
   * Limpia el caché de un recurso dinámico
   * @param {string} resource - Nombre del recurso
   * @param {string} cacheKey - Clave completa del caché
   */
  const clearDynamicCache = useCallback((resource, cacheKey) => {
    removeCachedData(cacheKey);
  }, []);

  const fetchTemplateData = useCallback(async (forceRefresh = false) => {
    if (!applicationId || !token) {
      setIsLoadingTemplate(false);
      return;
    }

    const config = CACHE_CONFIG.templateData;
    
    if (!config.enabled && !forceRefresh) {
      setIsLoadingTemplate(false);
      return;
    }

    try {
      setIsLoadingTemplate(true);
      setTemplateError(null);

      if (!forceRefresh) {
        const cachedData = getCachedData(config.key);
        if (cachedData) {
          setTemplateData(cachedData);
          setIsLoadingTemplate(false);
          
          setTimeout(() => {
            fetchTemplateDataInBackground();
          }, 0);
          
          return;
        }
      }

      const response = await marcaBlancaApiClient.get(marcaBlancaApi.getTemplateData(applicationId));
      
      const { updated, data } = updateCacheIfChanged(config.key, response, config.ttl);
      
      if (updated || !templateData) {
        setTemplateData(data);
      }
      
    } catch (error) {
      console.error('Error fetching template data:', error);
      setTemplateError(error.message || 'Error al cargar template data');
      
      const cachedData = getCachedData(config.key);
      if (cachedData) {
        setTemplateData(cachedData);
      }
    } finally {
      setIsLoadingTemplate(false);
    }
  }, [applicationId, token, templateData]);

  const fetchTemplateDataInBackground = useCallback(async () => {
    if (!applicationId || !token) {
      return;
    }

    const config = CACHE_CONFIG.templateData;

    try {
      const response = await marcaBlancaApiClient.get(marcaBlancaApi.getTemplateData(applicationId));
      
      const { updated, data } = updateCacheIfChanged(config.key, response, config.ttl);
      
      if (updated) {
        setTemplateData(data);
      }
      
    } catch (error) {
      console.warn('Error en background fetch de template data:', error);
    }
  }, [applicationId, token]);

  const refetchTemplate = useCallback(() => {
    return fetchTemplateData(true);
  }, [fetchTemplateData]);

  const clearTemplateCache = useCallback(() => {
    removeCachedData(CACHE_CONFIG.templateData.key);
    setTemplateData(null);
  }, []);

  useEffect(() => {
    if (reduxTemplateData && Object.keys(reduxTemplateData).length > 0 && !templateData) {
      const config = CACHE_CONFIG.templateData;
      updateCacheIfChanged(config.key, reduxTemplateData, config.ttl);
      setTemplateData(reduxTemplateData);
      setIsLoadingTemplate(false);
    }
  }, [reduxTemplateData, templateData]);

  useEffect(() => {
    if (isClient && applicationId && token) {
      fetchAccionesAplicacion();
      
      if (!reduxTemplateData || Object.keys(reduxTemplateData).length === 0) {
        fetchTemplateData();
      }
    }
  }, [applicationId, token, reduxTemplateData]);

  const values = {
    accionesAplicacion,
    isLoadingAcciones,
    accionesError,
    refetchAcciones,
    clearAccionesCache,
    templateData,
    isLoadingTemplate,
    templateError,
    refetchTemplate,
    clearTemplateCache,
    getCacheStatus,
    fetchWithCache,
    clearDynamicCache,
  };

  return (
    <CacheConfigContext.Provider value={values}>
      {children}
    </CacheConfigContext.Provider>
  );
};

export const useCacheConfig = () => {
  const context = useContext(CacheConfigContext);
  
  if (!context) {
    throw new Error('useCacheConfig must be used within CacheConfigProvider');
  }
  
  return context;
};

export default CacheConfigContext;
